import 'ol/ol.css';
import * as ol from 'ol';
import * as olLayer from 'ol/layer';
import * as olSource from 'ol/source';
import * as olStyle from 'ol/style';
import Draw from 'ol/interaction/Draw';
import * as olGeom from 'ol/geom';
import * as olFormat from 'ol/format';
import ContextMenu from 'ol-contextmenu';
import 'ol-contextmenu/dist/ol-contextmenu.css';
import Vue from 'vue';
import store from '../../store';
import vuetify from '../../plugins/vuetify';
import i18n from '../../plugins/i18n';
import DotSolarMapContext from './DotSolarMapContext.js';
import DotMapStyle from './DotMapStyle.js';
import DrawShapes from '../../modules/dot-d3/DrawShapes.js';
import MapFeatureDragHandler from './handler/MapFeatureDragHandler.js';
import { moduleDefectApi } from '../../apis/module.defect.api';
import {
  hasMeaningfulCoordsMove,
  syncDefectCoordsAdjustMinimal
} from '@/services/defect/defectCoordsAdjustSync.js';
import { getModuleDefectDetailCached } from '../../utils/moduleDefectDetailCache.js';
import { GeoImageCreator } from '../../utils/imagePolygonManager/geoImageCreator.js';
import { getRgbImageNaturalSize } from '../../utils/rgbImageNaturalSizeCache.js';
import imageCoordinateCalculator from '../../utils/imageCoordinateCalculator.js';
import { polygonTypeCalculator } from '../../utils/imagePolygonManager/polygonTypeCalculator.js';
import DefectOverlayPopupContent from './DefectOverlayPopupContent.vue';
import { processImageUrlForCdn } from '@/utils/imagePolygonManager/imageLoader';
import {
  logDefectStatusDistribution,
  normalizeDefectStatus
} from '@/services/defect/defectStatus.utils';
import { mergeConfirmedWfsRowsWithExistingAi } from '@/utils/defect/wfsDefectRowMerge';
import { WFS_DEFECT_SOURCE } from '@/features/defect-drawer/utils/defectFilter';
import { resolveSolarModuleWfsProperties } from '@/utils/map/resolveSolarModuleWfsProperties';
import {
  MAP_FEATURE_CLICK_KIND,
  classifyMapFeatureClick
} from '@/features/dashboard-map/hierarchy/mapFeatureClickPolicy';
import { loadAiDefectDedupState } from '@/utils/defect/aiDefectDedupStore.js';
import {
  defectOverlayQuadForDisplay,
  mergeDefectRecordForShapeOverlay,
  moduleDefectMatchesPk,
  resolveDefectShapeGeomForOverlay,
  resolveModuleDefectPk,
  resolveWfsSolarModuleSeq
} from '@/utils/defect/defectShapeOverlay.js';

const thermalImgZindex = 10;
const rgbImgZindex = 20;
const solarModuleZindex = 25;
const defectZindex = 30;
const defectFocusHighlightZindex = 35;
const DefectOverlayPopupContentConstructor = Vue.extend(DefectOverlayPopupContent);
/**
 * DotSolarMap
 * layer순위 : 1. baseLayer(0) 2.thermal/IR footprint·선택사각(10) 3. rgbImg(20) 4. solarModule(25) 5. defect·AI결함 WFS(30)
 */
const DotSolarMap = {
  solarModuleZindex,
  /** @private */ _solarModuleLayer: null,
  /**
   * 현재 표시 중인 Solar Module 레이어 참조 저장
   * @param {import('ol/layer/Vector').default|null} layer
   */
  setSolarModuleLayer(layer) {
    this._solarModuleLayer = layer;
  },
  /** @returns {import('ol/layer/Vector').default|null} */
  getSolarModuleLayer() {
    return this._solarModuleLayer;
  },
  map: undefined,
  view: {},
  center: [126.7844650507171, 35.016985475543684],
  siteId: undefined,

  // GeoImage 생성을 위한 공통 모듈 인스턴스 (ImagePolygonMapManager와 동일)
  geoImageCreator: new GeoImageCreator(),

  layers: undefined,
  source: undefined,
  // features: [],

  projection: 'EPSG:4326',
  overlay: undefined,
  overlayPopupVm: null,
  defectDrawer: null,
  /** 팝업 IR 박스 비동기 요청이 이전 클릭 결과로 덮이지 않도록 */
  _popupOutlineSeq: 0,
  editingHander: null,
  /** 맵 마커 수정(editingMapFlag) ON일 때만 열화상 선택 지오사각을 픽셀 히트에 포함 */
  markerEditMode: false,
  popup: {
    container: undefined,
    content: undefined,
    closer: undefined
  },

  workingLayer: {
    solarModule: undefined,
    rgbImg: undefined,
    rgbImgFeature: undefined,
    thermalImgFeature: undefined,
    defectFeature: undefined,
    defectFocusHighlight: undefined,
    aiDetectFeature: undefined,
    imgPosFeature: undefined,
    recommendImgPosFeature: undefined,
    rgbLayer: undefined,
    irLayer: undefined,
    geoJsonLayer: undefined
  },

  vectorSource: {
    imgPosFeature: undefined,
    recommendImgPosFeature: undefined
  },

  controls: {
    defectControl: undefined,
    contextMenu: undefined
  },
  moduleLayer: {},

  drawing: {
    layer: undefined,
    source: undefined,
    drawer: undefined
  },

  hasLayer(map, name) {
    let r = false;
    map.getLayers().forEach(layer => {
      if (layer.get('name') == name) {
        r = true;
      }
    });
    return r;
  },
  getLayer(map, name) {
    let r = undefined;
    map.getLayers().forEach(layer => {
      if (layer.get('name') == name) {
        r = layer;
      }
    });
    return r;
  },
  putLayer(map, name, layer) {
    const layers = map.getLayers();
    layer.set('name', name);
    layers.addLayer(layer);
  },
  /**
   *
   * @param {Number} mapElmId
   * @param {Object} mapOpts -> {mapsOn: [map1, map2, ...], zoom: 10, drawingOn: true, siteSeq: 1, reportSeq: 1,}
   */
  initMap(mapElmId, mapOpts) {
    if (this.map) {
      this.map.setTarget(undefined);
      this.map = undefined;
    }
    this.layers = DotSolarMapContext.getBaseLayers(mapOpts.mapsOn);
    console.log('this.layers', this.layers);
    this.layers.push(DotSolarMapContext.getSiteImageMapTileLayer());
    // RGB 이미지 레이어를 defect 레이어 상위로 올리기 위하여 layer의 ZIndex 값을 10로 설정함.
    this.workingLayer.rgbImg = new olLayer.Image();
    this.workingLayer.rgbImg.setZIndex(rgbImgZindex);
    this.layers.push(this.workingLayer.rgbImg);
    this.vectorSource.imgPosFeature = new olSource.Vector();

    this.workingLayer.imgPosFeature = new olLayer.Vector({
      source: this.vectorSource.imgPosFeature,
      style: DotMapStyle.getArrowStyle
    });
    this.vectorSource.recommendImgPosFeature = new olSource.Vector();
    this.workingLayer.recommendImgPosFeature = new olLayer.Vector({
      source: this.vectorSource.recommendImgPosFeature,
      style: DotMapStyle.getArrowStyle
    });

    //polygon을 GeoJson으로 만든다.
    // let geojsonObject = {
    //     'type': 'Feature',
    //     'geometry': {
    //         'type': 'Polygon',
    //         'coordinates': [
    //             [
    //                 [126.64406663471819, 36.539906111704305],
    //                 [126.64407170234568, 36.53991458619733],
    //                 [126.64407048648387, 36.539905600228764],
    //                 [126.64404842182601, 36.53990780926148],
    //             ]
    //         ],
    //     }
    // }

    // let vectorLayer = new olLayer.Vector({
    //     source: new olSource.Vector({
    //         features: new olFormat.GeoJSON().readFeatures(geojsonObject),
    //     }),
    // })

    // console.log("this layer", this.layers);
    // 현재 선택된 모듈을 그릴때 레이어가 쌓이는 것을 방지하기위해 하나의 레이어를 pop한 뒤 다시 push 한다. 때문에 초기화 작업에서 하나의 빈 layer를 layer array마지막에 넣어준다.
    // this.layers.push(vectorLayer)

    // 맵에 polygon을 그리는 옵션이 켜져 있는 경우
    if (mapOpts.drawingOn) {
      this.drawing.source = new olSource.Vector({
        wrapX: false
      });
      this.drawing.layer = new olLayer.Vector({
        source: this.drawing.source
      });
      this.layers.push(this.drawing.layer);

      // console.log("this.drawing: ", this.drawing);
      this.drawing.drawer = new Draw({
        source: this.drawing.source,
        type: 'Polygon'
      });
      this.drawing.drawer.on('drawend', () => {
        console.log('draw ended', this.drawing.drawer.sketchCoords_);
      });
    }

    // if (mapOpts.siteSeq) {
    //     this.workingLayer.solarModule = DotSolarMapContext.getSolarModuleLayer(mapOpts.siteSeq, mapOpts.reportSeq, DotMapStyle.solarModule);
    //     if(this.workingLayer.solarModule){
    //         this.layers.push(this.workingLayer.solarModule);
    //     }
    // }

    // if (mapOpts.reportSeq) {
    //     this.workingLayer.defectFeature = DotSolarMapContext.getSolarModuleDefectLayer(mapOpts.reportSeq, DotMapStyle.defectModule);
    //     this.layers.push(this.workingLayer.defectFeature);
    // }
    this.view = new ol.View({
      projection: this.projection,
      center: this.center,
      zoom: mapOpts.zoom
    });

    this.map = new ol.Map({
      target: mapElmId,
      layers: this.layers,
      pixelRatio: 1,
      view: this.view,
      // controls: olControl.defaults({
      //     mouseWheelZoom: false,
      //     attributionOptions: {
      //         collapsible: false
      //     }
      // })
      controls: [] // control 제거
      // interactions: mapOpts.editingOn ? defaultInteractions().extend([new MapFeatureDragHandler(this.adjustDefectLocation)]) : null,
    });

    // Canvas 성능 최적화 설정
    this.optimizeCanvasPerformance();

    if (mapOpts.drawingOn) {
      this.map.addInteraction(this.drawing.drawer);
    }

    // this.map.addInteraction(defaultInteractions().extend([new MapFeatureDragHandler()]));

    this.map.on('pointermove', evt => {
      if (evt.dragging) return;

      const hit = this.map.hasFeatureAtPixel(evt.pixel, {
        layerFilter: layer => this.hitTestLayerFilter(layer)
      });

      this.map.getTargetElement().style.cursor = hit ? 'pointer' : '';
    });

    // 이미지 마커를 표시할 Vector Layer를 추가한다.
    this.map.addLayer(this.workingLayer.imgPosFeature);
    this.map.addLayer(this.workingLayer.recommendImgPosFeature);

    // Context Menu 초기화
    this.initializeContextMenu();
  },

  /**
   * Context Menu 초기화
   * 오른쪽 마우스 클릭 시 해당 위치의 위경도 좌표를 context menu에 바로 표시
   */
  initializeContextMenu() {
    try {
      const contextMenu = new ContextMenu({
        width: 250,
        defaultItems: false // 기본 줌 인/아웃 아이템 제거
      });

      // Context menu가 열리기 전에 좌표 정보를 동적으로 추가
      contextMenu.on('beforeopen', event => {
        const coordinate = event.coordinate;
        const longitude = coordinate[0].toFixed(6);
        const latitude = coordinate[1].toFixed(6);

        // 기존 아이템 제거
        contextMenu.clear();

        // 위·경도 한 줄 표시, 해당 항목 클릭 시 클립보드 복사
        const coordinateLine = i18n.t('panelPlanning.contextMenuCoordinates', {
          lng: longitude,
          lat: latitude
        });
        contextMenu.extend([
          {
            text: coordinateLine,
            callback: () => {
              if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(`${longitude}, ${latitude}`).then(() => {
                  console.log('[DotSolarMap] 좌표가 클립보드에 복사되었습니다:', coordinateLine);
                });
              }
            }
          }
        ]);
      });

      // 맵에 컨텍스트 메뉴 추가
      this.map.addControl(contextMenu);
      this.controls.contextMenu = contextMenu;
    } catch (error) {
      console.error('[DotSolarMap] Context Menu 초기화 중 오류:', error);
    }
  },

  /**
   * Canvas 성능 최적화 설정
   * willReadFrequently 속성을 설정하여 getImageData() 성능 향상
   */
  optimizeCanvasPerformance() {
    try {
      // OpenLayers 맵의 Canvas 요소들에 willReadFrequently 설정
      const mapElement = this.map.getTargetElement();
      const canvasElements = mapElement.querySelectorAll('canvas');

      canvasElements.forEach(canvas => {
        if (canvas.getContext) {
          // 기존 context가 있다면 새로운 context로 교체
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          if (ctx) {
            console.log('[DotSolarMap] Canvas 성능 최적화 적용됨', {
              canvasId: canvas.id || 'unnamed',
              willReadFrequently: true
            });
          }
        }
      });

      // 맵 렌더링 완료 후 추가 Canvas 요소들도 최적화
      this.map.once('rendercomplete', () => {
        const additionalCanvasElements = mapElement.querySelectorAll('canvas');
        additionalCanvasElements.forEach(canvas => {
          if (canvas.getContext && !canvas._optimized) {
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            if (ctx) {
              canvas._optimized = true;
              console.log('[DotSolarMap] 추가 Canvas 성능 최적화 적용됨', {
                canvasId: canvas.id || 'unnamed'
              });
            }
          }
        });
      });

      console.log('[DotSolarMap] Canvas 성능 최적화 설정 완료');
    } catch (error) {
      console.error('[DotSolarMap] Canvas 성능 최적화 설정 실패', error);
    }
  },

  openDefectDetails(defectId) {
    console.log('defect ID : ' + defectId);
  },

  initOverlayPopup(
    containerElmId,
    contentElmId,
    closerElmId,
    imageContainerElmId,
    canvasElmId,
    contentTableElemId
  ) {
    this.popup.container = document.getElementById(containerElmId);
    this.popup.content = document.getElementById(contentElmId);
    this.popup.closer = document.getElementById(closerElmId);
    this.popup.imageContainer = document.getElementById(imageContainerElmId);
    this.popup.contentTable = document.getElementById(contentTableElemId);
    this.defectDrawer = new DrawShapes('#' + canvasElmId);

    this.overlay = new ol.Overlay({
      element: this.popup.container,
      autoPan: true,
      autoPanAnimation: {
        duration: 200
      },
      offset: [0, -10]
    });

    this.popup.closer.onclick = () => {
      this.destroyOverlayPopupComponent();
      this.setPopupThermalImageVisible(false);
      this.overlay.setPosition(undefined);
      this.popup.closer.blur();
      return false;
    };

    this.map.addOverlay(this.overlay);
    /**
     * map의 한 지점을 클릭했을 떄 feature가 있는지 확인하고 있으면 팝업을 띄운다.
     */
    this.map.on('singleclick', evt => {
      const feature = this.map.forEachFeatureAtPixel(evt.pixel, feature => feature, {
        layerFilter: layer => this.hitTestLayerFilter(layer)
      });
      if (feature) {
        try {
          // example data
          // {"type":"Feature","id":"module_defects.fid-517a228_1739d23346c_6377",
          // "geometry":{"type":"Point","coordinates":[126.644173,36.539648]},
          // "geometry_name":"solar_module_center_coord",
          // "properties":{"seq":2,"mission_report_seq":1,
          //               "solar_module_seq":4305,"thermal_img_seq":2,
          //                "defect_type":"CELL-DEFECT","defect_way":"U",
          //                 "mngt_status":"FND","img_name":"DJI_0021_R.JPG","rgb_image_seq":2},
          // "bbox":[126.644173,36.539648,126.644173,36.539648]}
          const properties = feature.getProperties();

          //지도상에서 상자 테두리를 클릭했을때 에러 방지 ()
          if (!feature?.getId()) return;

          const clickKind = classifyMapFeatureClick(feature);

          // siteDesign 모듈 폴리곤 — Drawer·하이라이트만, ol 팝오버 미표시
          if (clickKind === MAP_FEATURE_CLICK_KIND.MODULE) {
            this.dismissMapFeaturePopup();
            return;
          }

          // ID로 팝업에 표시할 내용을 출력한다.
          let imageTag = null;
          let content = '';

          let imgUrl = '';
          const isDefectPopupType = clickKind === MAP_FEATURE_CLICK_KIND.DEFECT;
          const isRgbPopupType = clickKind === MAP_FEATURE_CLICK_KIND.RGB;

          // img_url은 defect 팝업 타입에만 존재
          if (isDefectPopupType && properties.img_url) {
            const rawUrl =
              properties.img_url.split('/')[0] === ''
                ? process.env.VUE_APP_CDN_URL + properties.img_url
                : process.env.VUE_APP_CDN_URL + '/' + properties.img_url;
            imgUrl = processImageUrlForCdn(rawUrl);
          }

          if (isDefectPopupType) {
            const defectGeom = feature.getGeometry();
            const defectCoords = defectGeom ? defectGeom.getCoordinates() : null;
            Vue.prototype.$eventBus?.$emit('defect-module-geo-verify-click', {
              defectSeq: properties.seq,
              lng: Array.isArray(defectCoords) ? defectCoords[0] : null,
              lat: Array.isArray(defectCoords) ? defectCoords[1] : null,
              wfsSolarModuleSeq: resolveWfsSolarModuleSeq(properties),
              geometryName: feature.getGeometryName?.() ?? null
            });
            // max-width:433은 원본 이미지를 2/3 비율로 축소한 것임.
            imageTag =
              '<img class="map-popup-feature-img" style="max-width:433px" src="' +
              imgUrl +
              '" alt="">';
            // imageTag = '<img style="max-width:433px" src="http://1.214.117.226:7979/solar-images/thermal/20210727-0000000316/00001_T.jpg">';
            content = '';
          } else if (isRgbPopupType) {
            imageTag =
              '<img class="map-popup-feature-img" style="max-width:500px" src="' +
              processImageUrlForCdn(process.env.VUE_APP_CDN_URL + properties.img_url) +
              '" alt="">';
            content = '';
          }
          this.defectDrawer.clear();
          if (isDefectPopupType) {
            this.mountOverlayPopupComponent(properties, 'defect');
            this.setPopupThermalImageVisible(true);
          } else if (isRgbPopupType) {
            this.setPopupThermalImageVisible(false);
            this.mountOverlayPopupComponent(properties, 'rgb');
          } else {
            this.setPopupThermalImageVisible(false);
            this.destroyOverlayPopupComponent();
            this.popup.contentTable.innerHTML = content;
          }
          if (isDefectPopupType && imageTag) {
            this.popup.imageContainer.innerHTML = imageTag;
            const popupImg = this.popup.imageContainer.querySelector('img.map-popup-feature-img');
            const runOutline = () => this.scheduleThermalPopupDefectOutline(properties);
            if (popupImg) {
              if (popupImg.complete && popupImg.naturalWidth > 0) {
                runOutline();
              } else {
                popupImg.addEventListener('load', runOutline, { once: true });
              }
            } else {
              runOutline();
            }
          }
          const screenCoord = evt.coordinate;
          // console.log("marker position ====> ", evt);
          //     this.map.getView().animate({
          //         center:  screenCoord,
          //         duration: 1000
          // })
          this.overlay.setPosition(screenCoord); //해당 좌표에 popup 생성 및 지도에 가려서 안보이는경우 보이도록 설정됨(custom 메뉴부분을 감지못함)
          //좌표로 해당 위치를 중앙으로 정렬하는게 어렵다.

          // 팝업이 가리지 않도록 지도의 위치 이동
          setTimeout(() => {
            // 팝업의 위치가 현재 보이는 화면의 중심으로 올 수 있도록 조정
            const resolution = this.view.getResolution();

            //ani로 이동할 가로,세로 위치
            //위치기준은 화면높이 or 화면 가로길이    *0.3만큼 아래로 이동
            //메뉴가 가리지 않기위한 최소한의 높이를 300px, 가로를 265px으로 지정
            let widthMovePixel = this.map.getSize()[0] * 0.17;
            let heightMovePixel = this.map.getSize()[1] * 0.3;
            if (heightMovePixel < 300) {
              heightMovePixel = 300;
            }
            if (widthMovePixel < 265) {
              widthMovePixel = 265;
            }

            //화면의 절반 보다 heightmovePixel이 크면 밖으로 넘어감 그럴때는 화면의 절반보다 조금작게 설정
            if (widthMovePixel > this.map.getSize()[0] * 0.5) {
              widthMovePixel = this.map.getSize()[0] * 0.5 - 50;
            }

            if (heightMovePixel > this.map.getSize()[1] * 0.5) {
              heightMovePixel = this.map.getSize()[1] * 0.5 - 50;
            }

            this.view.animate({
              center: [
                screenCoord[0] + widthMovePixel * resolution,
                screenCoord[1] + heightMovePixel * resolution
              ], // screenCoord를 사용하여 가로와 세로 위치 모두 중앙으로 이동
              duration: 100 // 이동 속도 조절 (150ms)
            });
          }, 200);
        } catch (err) {
          console.error('[DotSolarMap:click] 팝업 실패:', err);
        }
      } else {
        this.destroyOverlayPopupComponent();
        this.setPopupThermalImageVisible(false);
        this.overlay.setPosition(undefined);
      }
    });
  },
  setMapDefectPopupHeading(popupType) {
    const el = document.getElementById('ol-popup-map-defect-heading');
    if (!el) {
      return;
    }
    if (popupType === 'defect') {
      el.textContent = i18n.t('defects.popup.mapPopupTitle');
      el.classList.remove('d-none');
      el.setAttribute('aria-hidden', 'false');
    } else {
      el.textContent = '';
      el.classList.add('d-none');
      el.setAttribute('aria-hidden', 'true');
    }
  },

  /**
   * 결함 팝업만 IR 미리보기(이미지+SVG)를 표시한다. 모듈·RGB 팝업은 리스트/메타만 보여준다.
   * @param {boolean} visible
   */
  setPopupThermalImageVisible(visible) {
    const container = document.getElementById('ol-popup-img-container');
    if (container) {
      container.style.display = visible ? '' : 'none';
    }
    if (visible) {
      return;
    }
    ++this._popupOutlineSeq;
    if (this.popup.imageContainer) {
      this.popup.imageContainer.innerHTML = '';
    }
    this.defectDrawer?.clear();
    const svg = document.getElementById('ol-popup-canvas');
    if (svg) {
      svg.innerHTML = '';
      svg.removeAttribute('width');
      svg.removeAttribute('height');
      svg.removeAttribute('viewBox');
      svg.style.width = '';
      svg.style.height = '';
      svg.style.left = '';
      svg.style.top = '';
    }
  },

  mountOverlayPopupComponent(properties, popupType) {
    this.destroyOverlayPopupComponent();

    if (popupType === 'module') {
      const wfsRows = store.state.dashboard?.wfsDefectRows || [];
      const moduleKey =
        properties.solar_module_seq ?? properties.module_id ?? properties.solarModuleSeq ?? null;
      console.log('[MODULE-POPUP-DEBUG] mountOverlayPopupComponent(module)', {
        moduleKey,
        properties: {
          array_id: properties.array_id,
          string_id: properties.string_id,
          module_id: properties.module_id,
          solar_module_seq: properties.solar_module_seq
        },
        wfsDefectRowCount: wfsRows.length,
        wfsSolarModuleSeqSample: wfsRows.slice(0, 5).map(r => r.solarModuleSeq),
        filteredByModuleKey: wfsRows.filter(d => String(d.solarModuleSeq) === String(moduleKey))
      });
    }

    const instance = new DefectOverlayPopupContentConstructor({
      store,
      vuetify,
      i18n,
      propsData: {
        defectProperties: properties,
        popupType
      }
    });

    const onOpenDetail = payload => {
      const detail =
        payload != null && typeof payload === 'object' ? payload : { defectId: payload };
      window.dispatchEvent(
        new CustomEvent('open-defect-window', {
          detail
        })
      );
    };
    const onClosePopup = () => {
      this.destroyOverlayPopupComponent();
      this.setPopupThermalImageVisible(false);
      this.overlay.setPosition(undefined);
    };
    instance.$on('open-detail', onOpenDetail);
    instance.$on('close-popup', onClosePopup);
    instance.__popupHandlers = { onOpenDetail, onClosePopup };
    instance.$mount();

    this.popup.contentTable.innerHTML = '';
    this.popup.contentTable.appendChild(instance.$el);
    this.overlayPopupVm = instance;
    this.setMapDefectPopupHeading(popupType);
  },
  destroyOverlayPopupComponent() {
    this.setMapDefectPopupHeading(null);

    if (!this.overlayPopupVm) {
      return;
    }

    const handlers = this.overlayPopupVm.__popupHandlers;
    if (handlers) {
      this.overlayPopupVm.$off('open-detail', handlers.onOpenDetail);
      this.overlayPopupVm.$off('close-popup', handlers.onClosePopup);
    } else {
      this.overlayPopupVm.$off('open-detail');
      this.overlayPopupVm.$off('close-popup');
    }
    if (this.overlayPopupVm.$el && this.overlayPopupVm.$el.parentNode) {
      this.overlayPopupVm.$el.parentNode.removeChild(this.overlayPopupVm.$el);
    }
    this.overlayPopupVm.$destroy();
    this.overlayPopupVm = null;
  },

  flyTo(location, done) {
    // location을 바로 사용
    if (!(location && location.length === 2)) {
      console.error('잘못된 location 형식:', location);
      return;
    }
    const _duration = 200;
    const _zoom = this.view.getZoom();
    let parts = 2;
    let called = false;
    function _callback(complete) {
      --parts;
      if (called) {
        return;
      }
      if (parts === 0 || !complete) {
        called = true;
        done(complete);
      }
    }
    // 즉시 이동 (컨텍스트 의존성 문제 회피)
    this.view.setCenter(location);
  },

  _ensureDefectFocusHighlightLayer() {
    if (!this.map) {
      return null;
    }
    if (!this.workingLayer.defectFocusHighlight) {
      this.workingLayer.defectFocusHighlight = new olLayer.Vector({
        source: new olSource.Vector(),
        style: this._defectFocusPointStyle.bind(this)
      });
      this.workingLayer.defectFocusHighlight.setZIndex(defectFocusHighlightZindex);
      this.workingLayer.defectFocusHighlight.set('interactive', false);
      this.workingLayer.defectFocusHighlight.set('isHighlightLayer', true);
      this.map.addLayer(this.workingLayer.defectFocusHighlight);
    }
    return this.workingLayer.defectFocusHighlight;
  },

  /** 결함 포커스 점 스타일 (시각용, 이벤트 없음) */
  _defectFocusPointStyle() {
    return new olStyle.Style({
      image: new olStyle.Circle({
        radius: 5,
        fill: new olStyle.Fill({ color: 'rgba(255, 82, 82, 0.85)' }),
        stroke: new olStyle.Stroke({ color: 'rgba(255, 255, 255, 0.95)', width: 2 })
      })
    });
  },

  clearDefectFocusHighlight() {
    const layer = this.workingLayer.defectFocusHighlight;
    const source = layer?.getSource?.();
    if (source) {
      source.clear();
    }
  },

  /**
   * 모듈리스트에서 방금 만든 결함 마커. WFS 재요청 전에 패널 중심에 붙인다.
   * @param {{ seq: number, defectType?: string, coordinate?: number[], solarModuleSeq?: number|string }} marker
   */
  upsertDefectMarker(marker) {
    const targetSeq = Number(marker?.seq);
    const lng = Number(marker?.coordinate?.[0]);
    const lat = Number(marker?.coordinate?.[1]);
    if (!Number.isFinite(targetSeq) || !Number.isFinite(lng) || !Number.isFinite(lat)) {
      return;
    }
    const source = this.workingLayer?.defectFeature?.getSource?.();
    if (!source) {
      return;
    }
    let feature = source.getFeatures().find(item => Number(item.get('seq')) === targetSeq);
    if (!feature) {
      feature = new ol.Feature();
      source.addFeature(feature);
    }
    feature.setId(`module_defects.local-${targetSeq}`);
    feature.set('seq', targetSeq);
    if (marker.defectType != null && marker.defectType !== '') {
      feature.set('defect_type', String(marker.defectType));
    }
    if (marker.solarModuleSeq != null && marker.solarModuleSeq !== '') {
      feature.set('solar_module_seq', marker.solarModuleSeq);
    }
    feature.setGeometry(new olGeom.Point([lng, lat]));
    this.map?.render();
  },

  /** 임시 seq 마커를 서버 id로 바꾼다. */
  retargetDefectMarkerSeq(fromSeq, toSeq, patch = {}) {
    const from = Number(fromSeq);
    const to = Number(toSeq);
    if (!Number.isFinite(from) || !Number.isFinite(to)) {
      return;
    }
    const source = this.workingLayer?.defectFeature?.getSource?.();
    const feature = source?.getFeatures?.().find(item => Number(item.get('seq')) === from);
    if (!feature) {
      return;
    }
    feature.setId(`module_defects.local-${to}`);
    feature.set('seq', to);
    if (patch.defectType != null && patch.defectType !== '') {
      feature.set('defect_type', String(patch.defectType));
    }
    this.map?.render();
  },

  /** 낙관적 삭제 — WFS 결함 마커를 지도에서 즉시 제거 */
  removeDefectMarkersBySeq(seq) {
    const targetSeq = Number(seq);
    if (!Number.isFinite(targetSeq)) {
      return;
    }
    const layers = [this.workingLayer?.defectFeature, this.workingLayer?.aiDetectFeature];
    layers.forEach(layer => {
      const source = layer?.getSource?.();
      if (!source) {
        return;
      }
      source.getFeatures().forEach(feature => {
        if (Number(feature.get('seq')) === targetSeq) {
          source.removeFeature(feature);
        }
      });
    });
    this.map?.render();
  },

  /**
   * 결함 리스트 지도 이동 — 결함 좌표에 작은 점 표시 (시각용, 이벤트 없음)
   * @param {{ lng?: number, lat?: number, solarModuleSeq?: number|string }} defect
   */
  focusDefectOnMap(defect) {
    if (!this.map || !this.view) {
      return;
    }
    const lng = Number(defect?.lng);
    const lat = Number(defect?.lat);

    this.clearDefectFocusHighlight();
    const highlightLayer = this._ensureDefectFocusHighlightLayer();
    if (!highlightLayer) {
      return;
    }

    if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
      console.log('[FLYTO-DEBUG] ④ focusDefectOnMap', {
        seq: defect?.seq,
        lng,
        lat,
        branch: 'no-coords'
      });
      return;
    }

    const highlightGeom = new olGeom.Point([lng, lat]);

    console.log('[FLYTO-DEBUG] ④ focusDefectOnMap', {
      seq: defect?.seq,
      lng,
      lat,
      branch: 'defect-point'
    });

    const highlightFeature = new ol.Feature({ highlight: 'defect-focus' });
    highlightFeature.setGeometry(highlightGeom);
    highlightLayer.getSource().addFeature(highlightFeature);
    this.flyTo([lng, lat], () => {});
    this.map?.render();
  },
  //esc누르면 닫히도록 설정하는 부분중 하나 .. (다른 모달을 닫을때 esc누르면 같이 닫힘..)
  closePopup() {
    this.dismissMapFeaturePopup();
    this.popup?.closer?.blur();
    return false;
  },

  /** ol-popup + 결함 드로어 미리보기 닫기 (모듈 클릭 등) */
  dismissMapFeaturePopup() {
    this.defectDrawer?.clear();
    this.setPopupThermalImageVisible(false);
    this.destroyOverlayPopupComponent();
    this.overlay?.setPosition(undefined);
  },
  /**
   *
   */
  initState() {
    // 기존 맵 인스턴스 정리 (페이지 이동 후 복귀 시 레이어 중복 방지)
    if (this.map) {
      // 모든 레이어 제거
      const layers = this.map.getLayers().getArray();
      while (layers.length > 0) {
        this.map.removeLayer(layers[0]);
      }
      // 맵 타겟 해제
      this.map.setTarget(undefined);
    }
    this.map = undefined;
    this.view = {};
    this.center = [126.7844650507171, 35.016985475543684];
    this.siteId = undefined;

    this.layers = undefined;
    this.source = undefined;
    // features: [],

    this.projection = 'EPSG:4326';
    this.overlay = undefined;
    this.overlayPopupVm = null;
    this.defectDrawer = null;
    this.editingHander = null;
    this.markerEditMode = false;
    this.popup = {
      container: undefined,
      content: undefined,
      closer: undefined
    };

    this.workingLayer = {
      solarModule: undefined,
      rgbImg: undefined,
      rgbImgFeature: undefined,
      thermalImgFeature: undefined,
      defectFeature: undefined,
      defectFocusHighlight: undefined,
      aiDetectFeature: undefined,
      imgPosFeature: undefined,
      recommendImgPosFeature: undefined,
      rgbLayer: undefined,
      irLayer: undefined,
      geoJsonLayer: undefined
    };

    this.vectorSource = {
      imgPosFeature: undefined,
      recommendImgPosFeature: undefined
    };

    this.controls = {
      defectControl: undefined
    };

    this.moduleLayer = {};

    this.drawing = {
      layer: undefined,
      source: undefined,
      drawer: undefined
    };
  },
  /**
   * 선택된 이미지의 좌표와 일치하는 위치로 이동한다.
   * workingLayer 는 현재 이동하는 곳에 그려질 일종의 타일이다.
   * geo server 로 요청하여 현재 사용중인 맵의 포멧에 맞게 정제된 layer 이다.
   * rotation 값을 받아서 지도를 회전시킨다.
   * wfs?service로 응답받는 바디 안에,
   * features 안에 마커의 정보도 들어가있다.
   * @props {Object} site
   * @props {Boolean} firstMount
   */
  async moveSite(site, firstMount) {
    this.site = site;

    // 기존 레이어 제거
    if (this.workingLayer.solarModule) {
      this.map.removeLayer(this.workingLayer.solarModule);
    }
    if (this.workingLayer.defectFeature) {
      this.map.removeLayer(this.workingLayer.defectFeature);
    }
    if (this.workingLayer.aiDetectFeature) {
      this.map.removeLayer(this.workingLayer.aiDetectFeature);
    }

    // 사이트 변경 시 지도 이동
    if (this.siteId === undefined || this.siteId !== site.id || firstMount) {
      this.siteId = site.id;
      this.flyTo(site.location.coordinates, () => {});
    }

    // Solar Module 레이어는 관리자 버튼으로 수동 제어
    // 자동 로드 비활성화
    // const features = await DotSolarMapContext.getSolarModuleLayer(
    //   site.id,
    //   reportSeq,
    //   DotMapStyle.solarModule
    // );
    // if (features) {
    //   this.workingLayer.solarModule = features;
    //   this.map.addLayer(this.workingLayer.solarModule);
    //   this.map.render();
    // }
    // 새로운 Source하였으나, 소스가 가지고 있는 url을 호출하지 않음.
    // this.workingLayer.solarModule.setSource(DotSolarMapContext.getSolarModuleFeatures(site.id));
    // DotSolarMapContext.getSolarModuleFeatures(site.id)
    //     .then((resp) => {
    //         console.log("[samuel]fetch solar module features result ====> ", resp);
    //         // 기존에 있는 Feature를 삭제한다.
    //         layer를 다시 생성하지 않고, features만 추가하면 redraw 하지 않아서 아래 소스는 폐기함.
    //         let source = this.workingLayer.solarModule.getSource();
    //         // this.workingLayer.rgbImgFeature.getSource().clear();
    //         source.clear();
    //         // source.addFeatures(resp.data);
    //         source.addFeatures(source.getFormat().readFeatures(resp.data))
    //         //this.workingLayer.rgbImgFeature.getSource().addFeatures(resp.data);
    //         // this.workingLayer.rgbImgFeature.getSource()
    //         //     .addFeatures(resp.data);
    //     })
    //     .catch((error) => {
    //         console.log("getSolarModuleFeatures error: ", error);
    //     });
  },
  /**
   * @defectFilters Array ,  빈 배열은  결함 fetch안된다. ->찾고자하는 필터를 전부 넣어야함
   */
  fetchDefectInfoLocation(reportSeq, defectFilters) {
    console.log(
      '[samuel]fetch defect features with reportSeq ====> ' + reportSeq,
      defectFilters,
      DotMapStyle.defectModule
    );
    console.log('[DEFECT-MAP-DEBUG] fetchDefectInfoLocation', {
      reportSeq,
      defectFiltersLen: defectFilters.length,
      defectFilters
    });
    if (this.workingLayer.defectFeature) {
      this.map.removeLayer(this.workingLayer.defectFeature);
    }
    this.workingLayer.defectFeature = DotSolarMapContext.getSolarModuleDefectLayer2(
      reportSeq,
      defectFilters,
      DotMapStyle.defectModule
    );
    this.workingLayer.defectFeature.setZIndex(defectZindex);
    this.map.addLayer(this.workingLayer.defectFeature);

    const defectSrc = this.workingLayer.defectFeature.getSource();
    const wfsLoadToken = `${reportSeq}-${Date.now()}`;
    defectSrc.on('featuresloadend', () => {
      const rawFeatures = defectSrc.getFeatures();
      const sampleRawProps = rawFeatures.slice(0, 3).map(f => {
        const p = f.getProperties();
        const keys = Object.keys(p).filter(k => k !== 'geometry');
        return {
          featureId: f.getId?.(),
          keys,
          solar_module_seq: p.solar_module_seq,
          solarModuleSeq: p.solarModuleSeq,
          module_seq: p.module_seq,
          module_id: p.module_id,
          resolvedSolarModuleSeq: resolveWfsSolarModuleSeq(p)
        };
      });
      const rows = rawFeatures.map(f => {
        const p = f.getProperties();
        const g = f.getGeometry();
        const c = g ? g.getCoordinates() : null;
        const moduleProps = resolveSolarModuleWfsProperties(p);
        return {
          seq: p.seq,
          solarModuleSeq: resolveWfsSolarModuleSeq(p),
          moduleId: moduleProps.moduleId ?? p.module_id ?? p.moduleId ?? null,
          stringId: moduleProps.stringId,
          arrayId: moduleProps.arrayId,
          solarStringSeq: p.solar_string_seq ?? p.solarStringSeq ?? null,
          solarArraySeq: p.solar_array_seq ?? p.solarArraySeq ?? null,
          thermalImgSeq: p.thermal_img_seq,
          defectType: p.defect_type,
          imgName: p.img_name,
          defectStatus: normalizeDefectStatus(p.mngt_status),
          mlDefectConfirmed: p.ml_defect_confirmed,
          /** detect_way: DB 컬럼명, GeoServer view 에 따라 'A'(AI) 또는 'M'(Manual) */
          detectWay: p.detect_way ?? p.defect_way ?? null,
          /** module_defects WFS — AI OFF여도 모듈 리스트에 표시 */
          wfsSource: WFS_DEFECT_SOURCE.CONFIRMED,
          /** mapPositionVerified: 사람이 지도 마커 위치를 검증했는지 (기본 false) */
          mapPositionVerified: false,
          defectDesc: p.defect_desc,
          defectCode: p.defect_code,
          lng: Array.isArray(c) ? c[0] : null,
          lat: Array.isArray(c) ? c[1] : null
        };
      });
      rows.sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
      const withModuleSeq = rows.filter(r => r.solarModuleSeq != null);
      const withoutModuleSeq = rows.filter(r => r.solarModuleSeq == null);
      logDefectStatusDistribution(rows, `WFS module_defects reportSeq=${reportSeq}`);
      const existing = store.state.dashboard?.wfsDefectRows || [];
      const mergedRows = mergeConfirmedWfsRowsWithExistingAi(rows, existing);
      store.commit('dashboard/setWfsDefectRows', mergedRows);
      store.commit('dashboard/setWfsDefectRowsReportId', reportSeq);
      console.log('[UNASSIGNED-PANEL-DEBUG] module_defects WFS → wfsDefectRows', {
        wfsLoadToken,
        reportSeq,
        featureCount: rawFeatures.length,
        confirmedCount: rows.length,
        preservedAiCount: mergedRows.length - rows.length,
        mergedTotal: mergedRows.length,
        withSolarModuleSeq: withModuleSeq.length,
        withoutSolarModuleSeq: withoutModuleSeq.length,
        allUnassigned: rows.length > 0 && withModuleSeq.length === 0,
        sampleRawWfsProperties: sampleRawProps,
        sampleRowsMissingModuleSeq: withoutModuleSeq.slice(0, 5).map(r => ({
          seq: r.seq,
          defectType: r.defectType,
          thermalImgSeq: r.thermalImgSeq
        })),
        serverCheckHint:
          withoutModuleSeq.length > 0
            ? 'GeoServer module_defects_by_new_type 뷰에 solar_module_seq 컬럼 포함·DB module_defect.solar_module_seq NOT NULL 확인 필요'
            : null
      });
      console.log('[DEFECT-MAP-DEBUG] module_defects WFS featuresloadend', {
        wfsLoadToken,
        reportSeq,
        featureCount: rawFeatures.length
      });
      console.log('[DEFECT-POSITION-DEBUG] WFS 지도 아이콘 좌표(전체 피처)', {
        wfsLoadToken,
        reportSeq,
        features: rows
      });
      Vue.prototype.$eventBus?.$emit('map-defect-layer-ready', {
        reportSeq,
        featureCount: rawFeatures.length
      });
    });
    defectSrc.on('featuresloaderror', err => {
      console.warn('[DEFECT-MAP-DEBUG] module_defects WFS featuresloaderror', err);
      Vue.prototype.$eventBus?.$emit('map-defect-layer-error', {
        reportSeq,
        error: String(err?.message ?? err)
      });
    });

    console.log('테스트 this.workingLayer.defectFeature', this.workingLayer.defectFeature);
  },

  fetchAiDetectDefectLocation(reportSeq, onFeaturesReady) {
    console.log('[samuel]fetch AI detected defects features with reportSeq ====> ' + reportSeq);
    if (this.workingLayer.aiDetectFeature) {
      this.map.removeLayer(this.workingLayer.aiDetectFeature);
    }
    this.workingLayer.aiDetectFeature = DotSolarMapContext.getSolarModuleAIDefectLayer(
      reportSeq,
      DotMapStyle.defectModule
    );
    this.workingLayer.aiDetectFeature.setZIndex(defectZindex);

    this.map.addLayer(this.workingLayer.aiDetectFeature);

    const defectSrc = this.workingLayer.aiDetectFeature.getSource();

    // AI 탐지 결함을 wfsDefectRows 에도 포함 → DefectListDrawer 필터에서 표시
    if (defectSrc) {
      defectSrc.on('featuresloadend', () => {
        const rawAiFeatures = defectSrc.getFeatures();
        const aiRows = rawAiFeatures.map(f => {
          const p = f.getProperties();
          const g = f.getGeometry();
          const c = g ? g.getCoordinates() : null;
          const moduleProps = resolveSolarModuleWfsProperties(p);
          return {
            seq: p.seq,
            solarModuleSeq: resolveWfsSolarModuleSeq(p),
            moduleId: moduleProps.moduleId ?? p.module_id ?? p.moduleId ?? null,
            stringId: moduleProps.stringId,
            arrayId: moduleProps.arrayId,
            solarStringSeq: p.solar_string_seq ?? p.solarStringSeq ?? null,
            solarArraySeq: p.solar_array_seq ?? p.solarArraySeq ?? null,
            thermalImgSeq: p.thermal_img_seq,
            defectType: p.defect_type,
            imgName: p.img_name,
            defectStatus: null,
            /** AI 탐지 결함은 detectWay = 'A' */
            detectWay: p.detect_way ?? p.defect_way ?? 'A',
            wfsSource: WFS_DEFECT_SOURCE.AI_ONLY,
            mapPositionVerified: false,
            defectDesc: p.defect_desc ?? '',
            defectCode: p.defect_code,
            lng: Array.isArray(c) ? c[0] : null,
            lat: Array.isArray(c) ? c[1] : null,
            mlDefectConfirmed: p.ml_defect_confirmed ?? null
          };
        });

        // 기존 wfsDefectRows 에 AI 결함 추가 (중복 제거)
        const existing = store.state.dashboard?.wfsDefectRows || [];
        const existingSeqs = new Set(existing.map(r => Number(r.seq)));
        const newAiRows = aiRows.filter(r => !existingSeqs.has(Number(r.seq)));
        if (newAiRows.length > 0) {
          store.commit('dashboard/setWfsDefectRows', [...existing, ...newAiRows]);
          console.log('[AI-WFS] AI 탐지 결함을 wfsDefectRows 에 추가:', newAiRows.length);
        }

        // 저장된 dedup(중복 숨김) 상태를 레이어 로드 때마다 재적용
        this.applyAiDefectDedupHiddenForReport(reportSeq);
        Vue.prototype.$eventBus?.$emit('map-ai-defect-layer-ready', {
          reportSeq,
          featureCount: rawAiFeatures.length
        });
      });
    }

    if (defectSrc && typeof onFeaturesReady === 'function') {
      const handler = () => {
        defectSrc.un('featuresloadend', handler);
        onFeaturesReady();
      };
      defectSrc.on('featuresloadend', handler);
    }
  },

  /**
   * AI WFS 마커 가시성만 조정(서버·피처 삭제 없음).
   * @param {Set<number>|null} visibleSeqSet
   * @param {number|null|undefined} linearRatio
   */
  applyAiDefectDisplayVisibility(visibleSeqSet, linearRatio) {
    const layer = this.workingLayer.aiDetectFeature;
    if (!layer) return;
    const src = layer.getSource();
    if (!src) return;
    const showAll =
      linearRatio == null || linearRatio >= 0.997 || !visibleSeqSet || visibleSeqSet.size === 0;
    const features = src.getFeatures();
    features.forEach(feature => {
      if (showAll) {
        feature.setStyle(null);
        return;
      }
      const props = feature.getProperties();
      const candidates = [
        props.seq,
        props.id,
        props.defect_seq,
        props.module_defect_seq,
        props.moduleDefectSeq
      ];
      let pk = null;
      for (let i = 0; i < candidates.length; i += 1) {
        const n = Number(candidates[i]);
        if (Number.isFinite(n) && n > 0) {
          pk = n;
          break;
        }
      }
      const visible = pk != null && visibleSeqSet.has(pk);
      feature.setStyle(visible ? null : []);
    });
    layer.changed();
  },

  removeAiDetectDefectLocation() {
    if (this.workingLayer.aiDetectFeature) {
      this.map.removeLayer(this.workingLayer.aiDetectFeature);
    }
  },

  /**
   * AI 중복검출 dedup — localStorage에 저장된 report별 숨김 seq 집합을 마커에 적용.
   * 서버·피처 삭제 없음: 숨김 seq만 스타일을 비워 지도에 한 결함당 하나만 보이게 한다.
   * @param {string|number} reportSeq
   */
  applyAiDefectDedupHiddenForReport(reportSeq) {
    const state = loadAiDefectDedupState(reportSeq);
    this.applyAiDefectDedupHidden(new Set(state.hiddenSeqs));
  },

  /** @param {Set<number>} hiddenSeqSet */
  applyAiDefectDedupHidden(hiddenSeqSet) {
    const layer = this.workingLayer.aiDetectFeature;
    if (!layer) return;
    const src = layer.getSource();
    if (!src) return;
    const hidden = hiddenSeqSet instanceof Set ? hiddenSeqSet : new Set();
    src.getFeatures().forEach(feature => {
      const seq = Number(feature.get('seq'));
      const isHidden = Number.isFinite(seq) && hidden.has(seq);
      feature.setStyle(isHidden ? [] : null);
    });
    layer.changed();
  },

  /**
   * 확정 결함 마커 레이어 on/off — 소스의 모든 피처에 동일 적용.
   * @param {boolean} visible
   */
  applyConfirmedDefectMarkerLayerVisibility(visible) {
    const layer = this.workingLayer?.defectFeature;
    if (!layer) return;
    layer.setVisible(!!visible);
    if (visible) {
      const src = layer.getSource();
      src?.getFeatures()?.forEach(feature => {
        feature.setStyle(null);
      });
    }
    layer.changed();
  },

  /**
   * AI 결함 마커 레이어 on/off — 소스의 모든 피처에 dedup 상태 재적용.
   * @param {boolean} visible
   * @param {string|number|null|undefined} reportSeq
   */
  applyAiDefectMarkerLayerVisibility(visible, reportSeq) {
    const layer = this.workingLayer?.aiDetectFeature;
    if (!layer) return;
    layer.setVisible(!!visible);
    if (visible && reportSeq != null && reportSeq !== '') {
      this.applyAiDefectDedupHiddenForReport(reportSeq);
    }
    layer.changed();
  },

  fetchImagePosition(reportSeq) {
    console.log('[samuel]fetch image position features with reportSeq ====> ' + reportSeq);
    // undefined가 아니면 기존에 있던 feature를 삭제한다.

    if (this.workingLayer.rgbImgFeature) {
      // this.workingLayer.rgbImgFeature = new olLayer.Vector({
      //     source: new olSource.Vector({
      //         format: new olFormat.GeoJSON()
      //     }),
      //     style: DotMapStyle.imageViewStyle
      // });
      // this.layers.push(this.workingLayer.rgbImgFeature);
      this.map.removeLayer(this.workingLayer.rgbImgFeature);
    }

    this.workingLayer.rgbImgFeature = DotSolarMapContext.getSolarRgbImageLayer(
      reportSeq,
      DotMapStyle.imageViewStyle
    );
    this.map.addLayer(this.workingLayer.rgbImgFeature);
    // DotSolarMapContext.getRgbImgFeatures(reportSeq)
    //     .then((resp) => {
    //         console.log("[samuel]fetch image position features result ====> ", resp);
    //         // 기존에 있는 Feature를 삭제한다.
    //         let source = this.workingLayer.rgbImgFeature.getSource();
    //         // this.workingLayer.rgbImgFeature.getSource().clear();
    //         source.clear();
    //         source.addFeatures(resp.data);
    //         // source.addFeatures(source.getFormat().readFeatures(resp.data))
    //         //this.workingLayer.rgbImgFeature.getSource().addFeatures(resp.data);
    //         // this.workingLayer.rgbImgFeature.getSource()
    //         //     .addFeatures(resp.data);
    //     })
    //     .catch((error) => {
    //         console.log("getRgbImgFeatures error: ", error);
    //     });
  },

  /*
   * Thermal geo 사각형을 지도에 표시한다.
   * thermalGeoObject: thermal 이미지의 위치 정보를 담은 geo 객체
   */
  displayThermalGeoRectangle(thermalGeoObject) {
    if (thermalGeoObject) {
      // 열화상 목록 클릭: DotSolarMapBox.changeImage 가 먼저 moveToObjectCenter 하므로 여기서는 이동 금지
      this.addFeature(thermalGeoObject, true, { recenter: false });
    }
  },

  /*
   * RGB 이미지를 맵 위에 제공한 좌표 위치에 배치하고, 이미지를 표시 여부에 따라 이미지를 표시한다.
   * thermalGeoObject: 호환성을 위해 유지하지만 사용하지 않음 (thermal geo 사각형은 displayThermalGeoRectangle로 별도 처리)
   * rgbImgUrl: 이미지 URL 경로
   * rgbGeoObject: 이미지가 배치될 정보를 가지고 있는 객체
   * imgVisible: 이미지를 보이게 할 것인지 말 것인지를 결정하는 Flag
   * knownPixelSize: API(solarRgbImages)가 준 imgWidth/imgHeight가 있으면 전달 — Image() 프로브 요청 1회 생략
   * @param {{ recenterAfterRgb?: boolean }} [options] recenterAfterRgb=false면 GeoImage 추가 후 moveToObjectCenter 생략(열화상 목록에서 이미 이동한 경우)
   */
  async putImageOnMap(
    thermalGeoObject,
    rgbImgUrl,
    rgbGeoObject,
    imgVisible,
    knownPixelSize,
    options = {}
  ) {
    const { recenterAfterRgb = true } = options;
    const resolvedRgbUrl = rgbImgUrl ? processImageUrlForCdn(rgbImgUrl) : rgbImgUrl;
    console.log('🔧 [RGB 이미지 보기] 열화상 조절 페이지와 동일한 GeoImage 방식 적용:', {
      rgbImgUrl: resolvedRgbUrl,
      imgVisible: imgVisible
    });

    if (!resolvedRgbUrl) {
      return false;
    }

    if (!rgbGeoObject || !rgbGeoObject.coordinates || !rgbGeoObject.coordinates[0]) {
      return false;
    }

    const coords = rgbGeoObject.coordinates[0];
    const ring =
      coords.length >= 4 &&
      coords[0][0] === coords[coords.length - 1][0] &&
      coords[0][1] === coords[coords.length - 1][1]
        ? coords
        : [...coords, coords[0]];

    const geometry = new olGeom.Polygon([ring]);
    const originalPolygonSize = polygonTypeCalculator.calculateAccuratePolygonSize(ring);

    if (!originalPolygonSize || originalPolygonSize.width <= 0 || originalPolygonSize.height <= 0) {
      console.warn('🔧 [RGB 이미지 보기] 폴리곤 크기 계산 실패, 기존 방식으로 fallback');
      return this.putImageOnMapFallback(
        rgbGeoObject,
        resolvedRgbUrl,
        imgVisible,
        knownPixelSize,
        options
      );
    }

    if (this.workingLayer.rgbImg) {
      this.map.removeLayer(this.workingLayer.rgbImg);
    }

    const finishWithPixelSize = async actualImageSize => {
      if (!actualImageSize) {
        console.warn('🔧 [RGB 이미지 보기] 이미지 크기 로드 실패, 기존 방식으로 fallback');
        return this.putImageOnMapFallback(
          rgbGeoObject,
          resolvedRgbUrl,
          imgVisible,
          knownPixelSize,
          options
        );
      }
      const imageSize = { width: actualImageSize.width, height: actualImageSize.height };
      const geoImageProperties = imageCoordinateCalculator.calculateGeoImageProperties(
        geometry,
        geometry,
        { width: originalPolygonSize.width, height: originalPolygonSize.height },
        imageSize
      );
      if (!geoImageProperties) {
        console.warn('🔧 [RGB 이미지 보기] GeoImage 속성 계산 실패, 기존 방식으로 fallback');
        return this.putImageOnMapFallback(
          rgbGeoObject,
          resolvedRgbUrl,
          imgVisible,
          knownPixelSize,
          options
        );
      }
      this.createGeoImageWithScale(
        resolvedRgbUrl,
        geoImageProperties.center,
        geoImageProperties.scale[0],
        geoImageProperties.scale[1],
        geoImageProperties.rotation,
        imgVisible,
        rgbGeoObject,
        { recenterAfterLayer: recenterAfterRgb }
      );
      console.log('✅ [RGB 이미지 보기] 폴리곤 기반 GeoImage(회전 지원) 생성 완료');
      return this.hasRgbImageOnMap();
    };

    const kw = knownPixelSize && knownPixelSize.width;
    const kh = knownPixelSize && knownPixelSize.height;
    if (kw > 0 && kh > 0) {
      return finishWithPixelSize({ width: kw, height: kh });
    }

    try {
      const actualImageSize = await this.loadActualImageSize(resolvedRgbUrl);
      return finishWithPixelSize(actualImageSize);
    } catch (err) {
      console.error('🔧 [RGB 이미지 보기] 오류:', err);
      return false;
    }
  },

  /**
   * RGB 이미지 지도 표시 실패 시 기존 extent+회전 계산 방식으로 재시도
   */
  async putImageOnMapFallback(rgbGeoObject, rgbImgUrl, imgVisible, knownPixelSize, options = {}) {
    if (!rgbGeoObject || !rgbGeoObject.coordinates || !rgbGeoObject.coordinates[0]) {
      return false;
    }
    const coords = rgbGeoObject.coordinates[0];
    const bottomLeftLng = coords[3][0];
    const bottomLeftLat = coords[3][1];
    const topRightLng = coords[1][0];
    const topRightLat = coords[1][1];
    const center = [(bottomLeftLng + topRightLng) / 2, (bottomLeftLat + topRightLat) / 2];
    const geoWidth = Math.abs(topRightLng - bottomLeftLng);
    const geoHeight = Math.abs(topRightLat - bottomLeftLat);
    await this.loadActualImageSizeAndCreateGeoImage(
      rgbImgUrl,
      center,
      geoWidth,
      geoHeight,
      imgVisible,
      rgbGeoObject,
      knownPixelSize,
      options
    );
    return this.hasRgbImageOnMap();
  },

  /*
   * 실제 이미지 크기를 로드한 후 GeoImage 생성
   * @param {string} rgbImgUrl - RGB 이미지 URL
   * @param {Array} center - 중심점 [lng, lat]
   * @param {number} geoWidth - 지리적 폭
   * @param {number} geoHeight - 지리적 높이
   * @param {boolean} imgVisible - 표시 여부
   * @param {Object} rgbGeoObject - 원본 GPS 객체 (moveToObjectCenter용)
   * @param {{ width: number, height: number }|undefined} knownPixelSize - API 픽셀 크기(있으면 프로브 생략)
   * @param {{ recenterAfterRgb?: boolean }} [options] createGeoImageWithScale로 전달
   */
  async loadActualImageSizeAndCreateGeoImage(
    rgbImgUrl,
    center,
    geoWidth,
    geoHeight,
    imgVisible,
    rgbGeoObject,
    knownPixelSize,
    options = {}
  ) {
    const { recenterAfterRgb = true } = options;
    try {
      const kw = knownPixelSize && knownPixelSize.width;
      const kh = knownPixelSize && knownPixelSize.height;
      let actualImageSize = kw > 0 && kh > 0 ? { width: kw, height: kh } : null;

      if (!actualImageSize) {
        console.log('📏 [3단계] 실제 이미지 크기 로딩 시작:', rgbImgUrl);
        actualImageSize = await this.loadActualImageSize(rgbImgUrl);
      }

      if (!actualImageSize) {
        console.warn('📏 이미지 크기 로드 실패, 기본값 사용');
        // 실패시 기본값으로 GeoImage 생성
        this.createGeoImageWithScale(
          rgbImgUrl,
          center,
          geoWidth / 2000,
          geoHeight / 1500,
          0,
          imgVisible,
          rgbGeoObject,
          {
            recenterAfterLayer: recenterAfterRgb
          }
        );
        return this.hasRgbImageOnMap();
      }

      // 📐 실제 이미지 크기로 정확한 스케일 계산
      const scaleX = geoWidth / actualImageSize.width;
      const scaleY = geoHeight / actualImageSize.height;

      // 🔄 [4단계] GPS 좌표로부터 회전각 계산
      console.log('🔄 [4단계] 회전각 계산 시작...');
      const rotation = this.calculateRotationFromGPS(rgbGeoObject);

      console.log('🔄 [4단계] 회전각 계산 완료:', {
        회전각_라디안: rotation.toFixed(6),
        회전각_도: ((rotation * 180) / Math.PI).toFixed(2) + '°'
      });

      console.log('📏 [3단계] 실제 이미지 크기 기반 스케일 계산:', {
        실제이미지크기: `${actualImageSize.width} x ${actualImageSize.height}`,
        지리적크기: `${geoWidth.toFixed(8)} x ${geoHeight.toFixed(8)}`,
        계산된스케일: `[${scaleX.toFixed(10)}, ${scaleY.toFixed(10)}]`
      });

      // GeoImage 생성
      this.createGeoImageWithScale(
        rgbImgUrl,
        center,
        scaleX,
        scaleY,
        rotation,
        imgVisible,
        rgbGeoObject,
        {
          recenterAfterLayer: recenterAfterRgb
        }
      );
      return this.hasRgbImageOnMap();
    } catch (error) {
      console.error('📏 실제 이미지 크기 기반 GeoImage 생성 중 오류:', error);
      // 오류 발생시 기본값으로 fallback
      this.createGeoImageWithScale(
        rgbImgUrl,
        center,
        geoWidth / 2000,
        geoHeight / 1500,
        0,
        imgVisible,
        rgbGeoObject,
        {
          recenterAfterLayer: recenterAfterRgb
        }
      );
      return this.hasRgbImageOnMap();
    }
  },

  /*
   * 계산된 스케일로 GeoImage 생성
   * @param {{ recenterAfterLayer?: boolean }} [options] true(기본)면 레이어 추가 후 moveToObjectCenter. 열화상 목록 경로에서는 false.
   */
  createGeoImageWithScale(
    rgbImgUrl,
    center,
    scaleX,
    scaleY,
    rotation,
    imgVisible,
    rgbGeoObject,
    options = {}
  ) {
    const { recenterAfterLayer = true } = options;
    try {
      // GeoImageSource 생성
      this.imgSource = this.geoImageCreator.createGeoImageSource(
        rgbImgUrl,
        center,
        [scaleX, scaleY],
        rotation
      );

      // GeoImageLayer 생성 및 맵에 추가
      this.workingLayer.rgbImg = this.geoImageCreator.createGeoImageLayer(this.imgSource, {
        name: 'RGB-GeoImage-Layer',
        opacity: 1.0,
        zIndex: rgbImgZindex
      });

      this.workingLayer.rgbImg.setVisible(imgVisible);
      this.map.addLayer(this.workingLayer.rgbImg);
      if (recenterAfterLayer) {
        this.moveToObjectCenter(rgbGeoObject);
      }

      console.log('✅ [3단계] 실제 해상도 기반 GeoImage 생성 완료');
    } catch (error) {
      console.error('GeoImage 생성 중 오류:', error);
    }
  },

  /*
   * 실제 이미지 크기 로드 (비동기)
   * @param {string} imageUrl - 이미지 URL
   * @returns {Promise<{width: number, height: number}|null>} 이미지 크기 또는 null
   */
  loadActualImageSize(imageUrl) {
    return getRgbImageNaturalSize(imageUrl).then(actualSize => {
      if (actualSize) {
        console.log('📏 실제 이미지 크기 로드 완료:', {
          url: imageUrl.substring(imageUrl.lastIndexOf('/') + 1),
          size: `${actualSize.width} x ${actualSize.height}`,
          ratio: (actualSize.width / actualSize.height).toFixed(3)
        });
      }
      return actualSize;
    });
  },

  /*
   * GPS 좌표로부터 회전각 계산
   * @param {Object} geoObject - GPS 좌표 정보 객체
   * @returns {number} 회전각 (라디안)
   */
  calculateRotationFromGPS(geoObject) {
    if (!geoObject || !geoObject.coordinates || !geoObject.coordinates[0]) {
      return 0;
    }

    const coords = geoObject.coordinates[0];
    if (coords.length < 4) {
      return 0;
    }

    try {
      // 사각형의 하단 변 벡터 계산 (좌하단 -> 우하단)
      // coords[3] = 좌하단, coords[0] = 우하단 (또는 다른 순서일 수 있음)

      // 여러 변을 시도해서 가장 긴 변을 찾기 (주 축 방향)
      const edges = [
        { from: coords[0], to: coords[1], name: '0->1' },
        { from: coords[1], to: coords[2], name: '1->2' },
        { from: coords[2], to: coords[3], name: '2->3' },
        { from: coords[3], to: coords[0], name: '3->0' }
      ];

      let longestEdge = edges[0];
      let maxLength = 0;

      edges.forEach(edge => {
        const dx = edge.to[0] - edge.from[0]; // 경도 차이
        const dy = edge.to[1] - edge.from[1]; // 위도 차이
        const length = Math.sqrt(dx * dx + dy * dy);

        if (length > maxLength) {
          maxLength = length;
          longestEdge = edge;
        }
      });

      // 가장 긴 변의 방향벡터로 회전각 계산
      const dx = longestEdge.to[0] - longestEdge.from[0];
      const dy = longestEdge.to[1] - longestEdge.from[1];

      // 동쪽(경도 증가 방향)을 기준(0°)으로 한 회전각
      const rotation = Math.atan2(dy, dx);

      console.log('🔄 GPS 회전각 계산:', {
        선택된_변: longestEdge.name,
        변_시작점: [longestEdge.from[0].toFixed(8), longestEdge.from[1].toFixed(8)],
        변_끝점: [longestEdge.to[0].toFixed(8), longestEdge.to[1].toFixed(8)],
        방향벡터: [dx.toFixed(10), dy.toFixed(10)],
        변_길이: maxLength.toFixed(10),
        계산된_회전각_라디안: rotation.toFixed(6),
        계산된_회전각_도: ((rotation * 180) / Math.PI).toFixed(2) + '°'
      });

      return rotation;
    } catch (error) {
      console.error('GPS 회전각 계산 중 오류:', error);
      return 0;
    }
  },

  /*
   * RGB 이미지를 지도상에서 제거한다.
   * Thermal geo 사각형은 유지한다.
   */
  removeImageOnMap() {
    this.imgSource = null;
    this.workingLayer.rgbImg.setVisible(false);
    // Thermal geo 사각형은 제거하지 않음 (displayThermalGeoRectangle로 별도 관리)
  },

  /*
   * 이미지가 선택되어있으면  파란색 Outline(항상 보여야함) or rgb이미지가 보이도록 한다.
   ** deprecated Outline은 그대로 유지한 상태에서 이미지만 보이지 않도록 설정한다.
   *
   */
  hasRgbImageOnMap() {
    return !!(this.imgSource && this.workingLayer?.rgbImg);
  },

  setThermalOutlineVisible(visible) {
    if (this.workingLayer?.thermalImgFeature) {
      this.workingLayer.thermalImgFeature.setVisible(!!visible);
    }
  },

  showImageOnMap(imgVisible) {
    const hasLayer = this.hasRgbImageOnMap();
    if (this.workingLayer?.rgbImg) {
      this.workingLayer.rgbImg.setVisible(!!imgVisible && hasLayer);
    }
  },

  setMapEditable(editableFlag) {
    this.markerEditMode = !!editableFlag;
    if (!this.map) {
      return;
    }
    if (editableFlag) {
      if (this.editingHander) {
        this.map.getInteractions().remove(this.editingHander);
      }
      this.editingHander = new MapFeatureDragHandler(this.adjustDefectLocation);
      this.map.getInteractions().extend([this.editingHander]);
    } else if (this.editingHander) {
      this.map.getInteractions().remove(this.editingHander);
      this.editingHander = null;
    }
  },

  /**
   * IR 선택 사각(thermalImgFeature) 등 displayOnly 레이어는 표시만 하고
   * 클릭·포인터 히트 테스트에서 제외한다 (아래 결함·모듈 선택으로 통과).
   */
  hitTestLayerFilter(layer) {
    if (layer === this.workingLayer?.thermalImgFeature || layer?.get?.('displayOnly') === true) {
      return false;
    }
    return true;
  },

  findDefectInThermalStore(defectPk, thermalImgSeq) {
    const thermals = store.getters['dashboard/getThermalImages'] || [];
    const thermal = thermals.find(t => Number(t?.id) === Number(thermalImgSeq));
    const list = thermal?.moduleDefects || [];
    return list.find(d => moduleDefectMatchesPk(d, defectPk)) || null;
  },

  resolvePopupThermalSize(defectItem, wfsProperties, popupImg) {
    const thermalImgSeq = wfsProperties?.thermal_img_seq;
    let thermal = defectItem?.solarThermalImage;
    if ((!thermal?.imgWidth || !thermal?.imgHeight) && thermalImgSeq) {
      const thermals = store.getters['dashboard/getThermalImages'] || [];
      thermal = thermals.find(t => Number(t?.id) === Number(thermalImgSeq)) || thermal;
    }
    if (!thermal) {
      thermal = {};
    }
    const nativeW = Math.max(
      1,
      Number(popupImg?.naturalWidth) ||
        Number(thermal.imgWidth) ||
        Number(wfsProperties?.img_width) ||
        Number(wfsProperties?.thermal_img_width) ||
        640
    );
    const nativeH = Math.max(
      1,
      Number(popupImg?.naturalHeight) ||
        Number(thermal.imgHeight) ||
        Number(wfsProperties?.img_height) ||
        Number(wfsProperties?.thermal_img_height) ||
        512
    );
    return { nativeW, nativeH };
  },

  syncPopupSvgToImage(img) {
    const container = document.getElementById('ol-popup-img-container');
    const svg = document.getElementById('ol-popup-canvas');
    if (!container || !svg || !img) {
      return { dw: 0, dh: 0 };
    }
    const cr = container.getBoundingClientRect();
    const ir = img.getBoundingClientRect();
    const dw = Math.max(2, Math.round(ir.width));
    const dh = Math.max(2, Math.round(ir.height));
    const left = ir.left - cr.left;
    const top = ir.top - cr.top;
    svg.style.position = 'absolute';
    svg.style.left = `${left}px`;
    svg.style.top = `${top}px`;
    svg.style.width = `${dw}px`;
    svg.style.height = `${dh}px`;
    svg.style.right = 'auto';
    svg.style.bottom = 'auto';
    svg.setAttribute('width', String(dw));
    svg.setAttribute('height', String(dh));
    svg.setAttribute('viewBox', `0 0 ${dw} ${dh}`);
    return { dw, dh };
  },

  /**
   * 지도 팝업 열화상 위에 결함 bbox(픽셀 좌표) 오버레이. REST 상세 + 표시 중인 img 크기로 스케일.
   * module_defects / ai_detect_defects 공통 (동일 moduleDefect 상세 API).
   */
  async scheduleThermalPopupDefectOutline(wfsProperties) {
    const outlineSeq = ++this._popupOutlineSeq;
    const defectPk = resolveModuleDefectPk(wfsProperties);
    if (!defectPk || !this.defectDrawer) return;

    let defectItem = null;
    try {
      defectItem = await getModuleDefectDetailCached(defectPk);
    } catch (e) {
      console.warn('[DEFECT-MAP] moduleDefect detail load failed', defectPk, e);
    }

    const fromStore = this.findDefectInThermalStore(defectPk, wfsProperties?.thermal_img_seq);

    let listRow = null;
    if (wfsProperties?.thermal_img_seq) {
      try {
        const list = await moduleDefectApi.getDefectsByThermalImageId(
          wfsProperties.thermal_img_seq,
          true
        );
        listRow = (list || []).find(d => moduleDefectMatchesPk(d, defectPk)) || null;
      } catch (e) {
        console.warn('[DEFECT-MAP] thermal defect list fallback failed', e);
      }
    }

    const merged = mergeDefectRecordForShapeOverlay(wfsProperties, defectItem, fromStore, listRow);
    merged.id = defectItem?.id ?? fromStore?.id ?? listRow?.id ?? defectPk;
    merged.seq = wfsProperties?.seq ?? merged.seq ?? defectPk;

    const drawWhenImgReady = () => {
      if (outlineSeq !== this._popupOutlineSeq) {
        return false;
      }
      const wrap = document.getElementById('ol-popup-img-wrapper');
      const img = wrap?.querySelector('img.map-popup-feature-img');
      if (!img || img.clientWidth < 2 || img.clientHeight < 2) {
        return false;
      }
      const { dw, dh } = this.syncPopupSvgToImage(img);
      if (dw < 2 || dh < 2) {
        return false;
      }
      const { nativeW, nativeH } = this.resolvePopupThermalSize(merged, wfsProperties, img);
      const scaledQuad = defectOverlayQuadForDisplay(merged, nativeW, nativeH, dw, dh);
      if (!scaledQuad || scaledQuad.length < 4) {
        const geom = resolveDefectShapeGeomForOverlay(merged);
        console.warn('[DEFECT-MAP] popup outline skipped — no drawable quad', {
          defectPk,
          geomType: geom?.type,
          rawGeomType: merged?.defectShapeGeom?.type
        });
        return false;
      }
      this.defectDrawer.clear();
      this.defectDrawer.drawMarkers([scaledQuad]);
      return true;
    };

    const retryDraw = attempts => {
      if (drawWhenImgReady()) return;
      if (attempts > 0) {
        requestAnimationFrame(() => retryDraw(attempts - 1));
      }
    };

    retryDraw(90);
  },

  /**
     * 원하는 위치의 geo 정보가 담긴 object를 받아 도형을 표기하는 함수
     * Format example)
     * shapeGeog:{
            type: "Polygon"
            coordinates: [
                [126.64409376352748, 36.5399123770916],
                [126.64411582470824, 36.539910167981844],
                [126.64411461579647, 36.53990118215101],
                [126.6440925511407, 36.539903391192006],
                [126.64409376352748, 36.5399123770916]
            ]
            crs: {
                properties: {name: "EPSG:4326"}
                type: "name"
            }
        }
     *
     * @param {object} geoObject GeoJSON-like geometry { type, coordinates }
     * @param {boolean} visible false면 열화상 하이라이트 레이어만 제거
     * @param {object} [options]
     * @param {boolean} [options.recenter=true] true면 레이어 추가 후 moveToObjectCenter(geoObject).
     *   열화상 목록 선택 시에는 DotSolarMapBox 가 이미 이동했으므로 displayThermalGeoRectangle 에서만 false.
     *   그 외 호출부는 옵션 생략 시 항상 이동(기본 true).
     */
  addFeature(geoObject, visible, options = {}) {
    const { recenter = true } = options;
    if (geoObject === null || !visible) {
      if (this.workingLayer.thermalImgFeature) {
        this.map.removeLayer(this.workingLayer.thermalImgFeature);
        this.workingLayer.thermalImgFeature = undefined;
      }
      return;
    }

    // IR 전체 레이어(thermalImageFeatureStyle)와 동일한 --solvit-map-ir-stroke 팔레트 + 헤일로
    const styles = DotMapStyle.thermalImageSelectedRectStyles();

    // 인자로 넘겨받은 값을 geoJson값으로 포멧한다.
    const geojsonObject = {
      type: 'Feature',
      geometry: {
        type: geoObject.type,
        coordinates: geoObject.coordinates
      }
    };

    const source = new olSource.Vector({
      features: new olFormat.GeoJSON().readFeatures(geojsonObject)
    });
    // 이전에 도형이 그려진 레이어를 제거한다.
    if (this.workingLayer.thermalImgFeature) {
      this.map.removeLayer(this.workingLayer.thermalImgFeature);
    }

    // 새로 선택된 도형을 레이어에 그린다. (표시 전용 — 클릭·드래그 등 상호작용 없음)
    this.workingLayer.thermalImgFeature = new olLayer.Vector({
      source: source,
      style: styles
    });
    this.workingLayer.thermalImgFeature.set('displayOnly', true);

    this.workingLayer.thermalImgFeature.setZIndex(thermalImgZindex);

    // 도형이 그려진 레이어를 지도에 더한다.
    this.map.addLayer(this.workingLayer.thermalImgFeature);
    if (recenter) {
      this.moveToObjectCenter(geoObject);
    }
  },

  /**
   * 선택 폴리곤(좌하·우상 코너 기준) 중심으로 지도를 짧게 애니메이션한다.
   *
   * 호출 경로:
   * 1. DotSolarMapBox changeImage — 열화상 선택 시 thermal imgPosCoords (목록 클릭 시 이동의 본체도 여기)
   * 2. addFeature(..., { recenter: true }) — 기본값; 열화상 하이라이트만 그리는 경로는 recenter:false 로 제외
   * 3. createGeoImageWithScale — RGB 레이어 올린 뒤 rgbGeoObject (옵션 recenterAfterLayer=false면 생략)
   *
   * 스킵: _isViewCenterNear — 이미 그 중심이면 animate 생략
   */
  moveToObjectCenter(geoObject) {
    const oneLon = geoObject.coordinates[0][3][0];
    const oneLat = geoObject.coordinates[0][3][1];
    const anotherLon = geoObject.coordinates[0][1][0];
    const anotherLat = geoObject.coordinates[0][1][1];
    const centerLon = (oneLon + anotherLon) / 2;
    const centerLat = (oneLat + anotherLat) / 2;

    if (this._isViewCenterNear(centerLon, centerLat)) {
      return;
    }

    this.view.animate({
      center: [centerLon, centerLat],
      duration: 150
    });
  },

  /**
   * 이미 해당 중심 근처면 moveToObjectCenter 애니메이션 생략
   */
  _isViewCenterNear(centerLon, centerLat) {
    if (!this.map || !this.view) {
      return false;
    }
    const currentCenter = this.map.getView().getCenter();
    if (!currentCenter || currentCenter.length < 2) {
      return false;
    }
    const tolerance = 0.0000001;
    return (
      Math.abs(currentCenter[0] - centerLon) < tolerance &&
      Math.abs(currentCenter[1] - centerLat) < tolerance
    );
  },

  moveToBelowCenter() {
    const _center = this.map.getView().getCenter();
    const _resolution = this.map.getView().getResolution();
    this.map.getView().animate();
  },

  mousewheel() {
    // console.log("mousewheel");
  },
  pointermove(evt) {
    if (evt.dragging) return;

    const hit = this.map.hasFeatureAtPixel(evt.pixel);

    this.map.getTargetElement().style.cursor = hit ? 'pointer' : '';
  },
  movestart() {
    // console.log('movestart');
  },
  moveend() {
    // console.log("moveend");
  },
  singleclick(evt) {
    console.log('singleclick', evt);
  },
  change() {
    console.log('change');
  },
  rendercomplete() {
    // console.log("rendercomplete");
  },
  postrender() {
    // console.log("postrender");
  },

  async adjustDefectLocation(defectId, lon, lat, context = {}) {
    if (!defectId || defectId === null || defectId === undefined) {
      console.warn('[DOT SOLAR MAP] adjustDefectLocation: defectId가 유효하지 않습니다:', defectId);
      return;
    }

    const { feature, startLon, startLat } = context;
    if (!hasMeaningfulCoordsMove(startLon, startLat, lon, lat)) {
      return;
    }

    console.log('[DOT SOLAR MAP] adjustDefectLocation 호출:', { defectId, lon, lat });
    try {
      await moduleDefectApi.adjustDefectLocation(defectId, {
        moduleDefectId: defectId,
        lon,
        lat
      });
    } catch (error) {
      if (feature?.getGeometry?.() && startLon != null && startLat != null) {
        feature.getGeometry().setCoordinates([startLon, startLat]);
      }
      console.error('[DOT SOLAR MAP] adjustDefectLocation PATCH 실패:', error);
      return;
    }

    try {
      const detail = await syncDefectCoordsAdjustMinimal(store, defectId, lon, lat, feature);
      const bus = Vue.prototype.$eventBus;
      const props = feature?.getProperties?.() || {};
      bus?.$emit('defect-coords-adjusted', {
        markerEditMode: this.markerEditMode,
        defectId,
        lon,
        lat,
        detail,
        thermalImgSeq:
          detail?.solarThermalImage?.id ?? props.thermal_img_seq ?? props.thermalImgSeq ?? null
      });
    } catch (error) {
      if (feature?.getGeometry?.()) {
        feature.getGeometry().setCoordinates([lon, lat]);
      }
      console.error('[DOT SOLAR MAP] adjustDefectLocation 동기화 실패:', error);
    }
  },

  prepareImageMarkerLayer() {
    this.vectorSource.imgPosFeature.clear();
    this.vectorSource.recommendImgPosFeature.clear();
  },

  displayImageMark(points, recommendPoints) {
    if (!points || points.length === 0) {
      return;
    }
    const self = this;
    const recommendMarkerArray = [];
    let recommendMarkerCount = 0;
    if (recommendPoints && recommendPoints.length > 0) {
      recommendPoints.map(recommendPoint => {
        if (!recommendPoint || !recommendPoint.lon || !recommendPoint.lat) return;
        const markerFeature = new ol.Feature({
          geometry: new olGeom.Point([recommendPoint.lon, recommendPoint.lat]),
          id: recommendPoint.lon + '_' + recommendPoint.lat
        });
        markerFeature.setStyle(DotMapStyle.createRecommendMarkerStyle('needFilm'));
        recommendMarkerArray.push(markerFeature);

        recommendMarkerCount++;
        if (recommendMarkerCount === recommendPoints.length) {
          self.vectorSource.recommendImgPosFeature.addFeatures(recommendMarkerArray);
          // self.workingLayer.recommendImgPosFeature.changed();
          // self.flyTo([recommendPoints.lon, recommendPoints.lat], () => {});
        }
      });
    }

    const markerArray = [];
    let markerCount = 0;
    points.map(point => {
      const markerFeature = new ol.Feature({
        geometry: new olGeom.Point([point.pos.lon, point.pos.lat]),
        id: point.name
      });
      markerFeature.setStyle(DotMapStyle.createMarkerStyle(point.name));
      markerArray.push(markerFeature);

      markerCount++;
      if (markerCount === points.length) {
        self.vectorSource.imgPosFeature.addFeatures(markerArray);
        self.workingLayer.imgPosFeature.changed();
        self.map.render();
        self.flyTo([point.pos.lon, point.pos.lat], () => {});
      }
    });

    let prevPoint = points[0];
    const lineFeatureArray = [];
    for (let ind = 1; ind <= points.length - 1; ind++) {
      const nextPoint = points[ind];
      // console.log('next point: ', nextPoint);

      const lineFeature = new ol.Feature({
        geometry: new olGeom.LineString([
          [prevPoint.pos.lon, prevPoint.pos.lat],
          [nextPoint.pos.lon, nextPoint.pos.lat]
        ]),
        name: nextPoint.distance.toFixed(0) + 'cm'
      });
      lineFeatureArray.push(lineFeature);
      prevPoint = nextPoint;
    }

    self.vectorSource.imgPosFeature.addFeatures(lineFeatureArray);
    self.workingLayer.imgPosFeature.changed();
    self.map.render();
  },

  /**
   * RGB Layer 토글
   * @param {Number} reportSeq - 보고서 시퀀스
   * @param {Boolean} visible - 표시 여부
   */
  toggleRgbLayer(reportSeq, visible) {
    if (visible) {
      this.addRgbLayer(reportSeq);
    } else {
      this.removeRgbLayer();
    }
  },

  /**
   * IR Layer 토글
   * @param {Number} reportSeq - 보고서 시퀀스
   * @param {Boolean} visible - 표시 여부
   */
  toggleIrLayer(reportSeq, visible) {
    if (visible) {
      this.addIrLayer(reportSeq);
    } else {
      this.removeIrLayer();
    }
  },

  /**
   * RGB Layer 추가
   * @param {Number} reportSeq - 보고서 시퀀스
   */
  addRgbLayer(reportSeq) {
    if (this.workingLayer.rgbLayer) {
      this.map.removeLayer(this.workingLayer.rgbLayer);
    }

    this.workingLayer.rgbLayer = DotSolarMapContext.getRgbImageFeatureCatalogLayer(reportSeq, () =>
      DotMapStyle.rgbImageFeatureStyle()
    );
    this.workingLayer.rgbLayer.setZIndex(rgbImgZindex);
    this.map.addLayer(this.workingLayer.rgbLayer);
    console.log('RGB Layer 추가됨:', reportSeq);
  },

  /**
   * IR Layer 추가
   * @param {Number} reportSeq - 보고서 시퀀스
   */
  addIrLayer(reportSeq) {
    if (this.workingLayer.irLayer) {
      this.map.removeLayer(this.workingLayer.irLayer);
    }

    this.workingLayer.irLayer = DotSolarMapContext.getThermalImageFeatureCatalogLayer(
      reportSeq,
      () => DotMapStyle.thermalImageFeatureStyle()
    );
    this.workingLayer.irLayer.setZIndex(thermalImgZindex);
    this.map.addLayer(this.workingLayer.irLayer);
    console.log('IR Layer 추가됨:', reportSeq);
  },

  /**
   * RGB Layer 제거
   */
  removeRgbLayer() {
    if (this.workingLayer.rgbLayer) {
      this.map.removeLayer(this.workingLayer.rgbLayer);
      this.workingLayer.rgbLayer = undefined;
      console.log('RGB Layer 제거됨');
    }
  },

  /**
   * IR Layer 제거
   */
  removeIrLayer() {
    if (this.workingLayer.irLayer) {
      this.map.removeLayer(this.workingLayer.irLayer);
      this.workingLayer.irLayer = undefined;
      console.log('IR Layer 제거됨');
    }
  },

  /**
   * GeoJSON 레이어 추가
   * @param {olLayer.Vector} vectorLayer - GeoJSON Vector Layer
   */
  addGeoJsonLayer(vectorLayer) {
    // 기존 GeoJSON 레이어 제거
    this.removeGeoJsonLayer();

    // 새 레이어 설정
    vectorLayer.setZIndex(50); // 다른 레이어들보다 위에 표시
    vectorLayer.set('name', 'geoJsonLayer');

    // 지도에 추가
    this.map.addLayer(vectorLayer);
    this.workingLayer.geoJsonLayer = vectorLayer;

    console.log('GeoJSON 레이어 추가됨');
  },

  /**
   * GeoJSON 레이어 제거
   */
  removeGeoJsonLayer() {
    if (this.workingLayer.geoJsonLayer) {
      this.map.removeLayer(this.workingLayer.geoJsonLayer);
      this.workingLayer.geoJsonLayer = undefined;
      console.log('GeoJSON 레이어 제거됨');
    }
  },

  /**
   * GeoJSON 레이어의 범위로 지도 이동
   * @param {olLayer.Vector} vectorLayer - GeoJSON Vector Layer
   */
  fitToGeoJsonLayer(vectorLayer) {
    try {
      const source = vectorLayer.getSource();
      const features = source.getFeatures();

      if (features.length === 0) {
        console.warn('GeoJSON 레이어에 feature가 없습니다.');
        return;
      }

      // 모든 feature의 extent 계산
      const extent = source.getExtent();

      if (extent && extent.length === 4) {
        // extent가 유효한 경우
        const view = this.map.getView();
        view.fit(extent, {
          padding: [50, 50, 50, 50], // 여백 추가
          duration: 1000, // 1초 애니메이션
          maxZoom: 18 // 최대 줌 레벨 제한
        });

        console.log('GeoJSON 레이어 범위로 지도 이동 완료:', extent);
      } else {
        // extent가 없는 경우 첫 번째 feature의 중심으로 이동
        const firstFeature = features[0];
        const geometry = firstFeature.getGeometry();
        const center = geometry.getExtent();

        if (center && center.length === 4) {
          const centerX = (center[0] + center[2]) / 2;
          const centerY = (center[1] + center[3]) / 2;

          this.map.getView().animate({
            center: [centerX, centerY],
            zoom: 15,
            duration: 1000
          });

          console.log('첫 번째 feature 중심으로 지도 이동 완료:', [centerX, centerY]);
        }
      }
    } catch (error) {
      console.error('GeoJSON 레이어 위치로 이동 중 오류:', error);
    }
  },

  /**
   * 라이트/다크 전환 시 이미지 위치 마커·연결선 등 피처 단위 스타일을 테마 변수 기준으로 다시 적용한다.
   * (레이어 style 콜백은 다음 렌더에서 갱신되지만, setStyle으로 고정된 피처는 재적용이 필요함)
   */
  refreshStylesForTheme() {
    if (!this.map || !this.vectorSource?.imgPosFeature) return;
    this.vectorSource.imgPosFeature.getFeatures().forEach(f => {
      const geom = f.getGeometry();
      if (!geom) return;
      const t = geom.getType();
      if (t === 'LineString') {
        f.setStyle(DotMapStyle.getArrowStyle(f));
      } else if (t === 'Point') {
        const markerName = f.get('id') ?? '';
        f.setStyle(DotMapStyle.createMarkerStyle(markerName));
      }
    });
    if (this.vectorSource.recommendImgPosFeature) {
      this.vectorSource.recommendImgPosFeature.getFeatures().forEach(f => {
        f.setStyle(DotMapStyle.createRecommendMarkerStyle('needFilm'));
      });
    }
    if (this.workingLayer.imgPosFeature?.changed) {
      this.workingLayer.imgPosFeature.changed();
    }
    if (this.workingLayer.recommendImgPosFeature?.changed) {
      this.workingLayer.recommendImgPosFeature.changed();
    }
    ['rgbLayer', 'irLayer', 'rgbImgFeature', 'geoJsonLayer'].forEach(key => {
      const layer = this.workingLayer[key];
      if (layer && typeof layer.changed === 'function') {
        layer.changed();
      }
    });
    // 이미지 선택 IR 사각형(thermalImgFeature): 스타일이 생성 시점에 고정되므로 테마 전환 시 재적용
    if (this.workingLayer.thermalImgFeature) {
      this.workingLayer.thermalImgFeature.setStyle(DotMapStyle.thermalImageSelectedRectStyles());
      this.workingLayer.thermalImgFeature.changed();
    }
    this.map.render();
  }
};

export default DotSolarMap;
